import type { ReactNode, ChangeEvent, FC } from 'react';
import { useState } from 'react';
import { useEditor } from '@craftjs/core';
import { HexColorPicker } from 'react-colorful';
import { ImageIcon, Upload, Trash2 } from 'lucide-react';
import { Label } from '../../../ui/label';
import { Input } from '../../../ui/input';
import { Switch } from '../../../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import { Textarea } from '../../../ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { Button } from '../../../ui/button';
import { uploadMedia, deleteMedia } from '../../../../services/blog.service';
import { STORAGE_KEYS } from '../../../../config';
import { cn } from '../../../../lib/utils';
import { LAYOUT_PRESETS } from './ColumnsBlock';

interface SelectedNodeInfo {
  id: string;
  displayName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>;
  /** For linked child nodes (e.g. ColumnContainer), the parent to delete instead */
  deleteTargetId: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyProps = Record<string, any>;
type SetPropFn = (cb: (p: AnyProps) => void) => void;

// ---- Reusable setting primitives ----

function SettingRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function RangeInput({
  min,
  max,
  step,
  value,
  onChange,
}: {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(Number(e.target.value))}
        className="flex-1 h-2 accent-primary cursor-pointer"
      />
      <span className="text-xs w-10 text-right tabular-nums">{value}</span>
    </div>
  );
}

function ColorField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="w-full justify-start gap-2">
          <span
            className="inline-block w-4 h-4 rounded border"
            style={{ backgroundColor: value }}
          />
          <span className="font-mono text-xs">{value}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <HexColorPicker color={value} onChange={onChange} />
        <Input
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          className="mt-2 font-mono text-xs"
          maxLength={7}
        />
      </PopoverContent>
    </Popover>
  );
}

// ---- Per-block settings ----

function TextBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  return (
    <>
      <SettingRow label="Tamanho da fonte (px)">
        <RangeInput
          min={10}
          max={72}
          step={1}
          value={props.fontSize ?? 16}
          onChange={(v) => setProp((p) => { p.fontSize = v; })}
        />
      </SettingRow>
      <SettingRow label="Alinhamento">
        <Select
          value={props.textAlign ?? 'left'}
          onValueChange={(v: string) => setProp((p) => { p.textAlign = v; })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Esquerda</SelectItem>
            <SelectItem value="center">Centro</SelectItem>
            <SelectItem value="right">Direita</SelectItem>
            <SelectItem value="justify">Justificado</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>
      <SettingRow label="Fonte">
        <Input
          value={props.fontFamily ?? 'inherit'}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.fontFamily = e.target.value; })}
          className="h-8 text-xs"
          placeholder="inherit"
        />
      </SettingRow>
      <SettingRow label="Cor do texto">
        <ColorField
          value={props.color ?? '#ffffff'}
          onChange={(v) => setProp((p) => { p.color = v; })}
        />
      </SettingRow>
    </>
  );
}

function HeadingBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  return (
    <>
      <SettingRow label="Nível do título">
        <Select
          value={String(props.level ?? 2)}
          onValueChange={(v: string) => setProp((p) => { p.level = Number(v); })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">H1 — Título Principal</SelectItem>
            <SelectItem value="2">H2 — Subtítulo</SelectItem>
            <SelectItem value="3">H3 — Seção</SelectItem>
            <SelectItem value="4">H4 — Subseção</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>
      <SettingRow label="Alinhamento">
        <Select
          value={props.textAlign ?? 'left'}
          onValueChange={(v: string) => setProp((p) => { p.textAlign = v; })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Esquerda</SelectItem>
            <SelectItem value="center">Centro</SelectItem>
            <SelectItem value="right">Direita</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>
      <SettingRow label="Fonte">
        <Input
          value={props.fontFamily ?? 'inherit'}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.fontFamily = e.target.value; })}
          className="h-8 text-xs"
          placeholder="inherit"
        />
      </SettingRow>
      <SettingRow label="Cor">
        <ColorField
          value={props.color ?? '#ffffff'}
          onChange={(v) => setProp((p) => { p.color = v; })}
        />
      </SettingRow>
    </>
  );
}

function ImageBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      // Delete previous media if replacing
      if (props.mediaId) {
        deleteMedia(props.mediaId).catch(() => {/* best effort */});
      }
      const media = await uploadMedia(file);
      setProp((p) => { p.src = media.url; p.mediaId = media.id; });
    } catch {
      setError('Falha ao enviar imagem.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <>
      <SettingRow label="Imagem">
        <div className="space-y-2">
          {props.src ? (
            <div className="relative rounded-md overflow-hidden border aspect-video bg-muted">
              <img
                src={props.src}
                alt={props.alt || 'Preview'}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-20 border border-dashed rounded-md bg-muted/50 text-muted-foreground">
              <ImageIcon className="h-6 w-6 mb-1" />
              <span className="text-xs">Nenhuma imagem</span>
            </div>
          )}
          <label className="block">
            <input
              type="file"
              accept="image/*"
              className="sr-only"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              asChild
              disabled={uploading}
            >
              <span>
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Enviando...' : props.src ? 'Trocar imagem' : 'Enviar imagem'}
              </span>
            </Button>
          </label>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <button
            type="button"
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showUrlInput ? '▾ fechar URL manual' : '▸ ou colar URL'}
          </button>
          {showUrlInput && (
            <Input
              value={props.src ?? ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.src = e.target.value; p.mediaId = ''; })}
              className="h-8 text-xs"
              placeholder="https://..."
            />
          )}
        </div>
      </SettingRow>
      <SettingRow label="Texto alternativo (alt)">
        <Input
          value={props.alt ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.alt = e.target.value; })}
          className="h-8 text-xs"
          placeholder="Descrição da imagem"
        />
      </SettingRow>
      <SettingRow label="Legenda">
        <Input
          value={props.caption ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.caption = e.target.value; })}
          className="h-8 text-xs"
          placeholder="Legenda opcional"
        />
      </SettingRow>
      <SettingRow label="Ajuste da imagem">
        <Select
          value={props.objectFit ?? 'cover'}
          onValueChange={(v: string) => setProp((p) => { p.objectFit = v; })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cover">Cobrir</SelectItem>
            <SelectItem value="contain">Conter</SelectItem>
            <SelectItem value="fill">Preencher</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>
      <SettingRow label="Borda arredondada (px)">
        <RangeInput
          min={0}
          max={48}
          step={2}
          value={props.borderRadius ?? 0}
          onChange={(v) => setProp((p) => { p.borderRadius = v; })}
        />
      </SettingRow>
    </>
  );
}

function VideoBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  const [videoMode, setVideoMode] = useState<'url' | 'upload'>(props.videoSource === 'upload' ? 'upload' : 'url');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleVideoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const media = await uploadMedia(file);
      setProp((p) => { p.url = media.url; p.videoSource = 'upload'; });
    } catch {
      setUploadError('Falha ao enviar o vídeo.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <SettingRow label="Origem do vídeo">
        <div className="flex rounded-md border overflow-hidden">
          <button
            type="button"
            onClick={() => { setVideoMode('url'); setProp((p) => { p.videoSource = 'url'; }); }}
            className={cn(
              'flex-1 px-3 py-1.5 text-xs font-medium transition-colors',
              videoMode === 'url'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            YouTube / Vimeo
          </button>
          <button
            type="button"
            onClick={() => setVideoMode('upload')}
            className={cn(
              'flex-1 px-3 py-1.5 text-xs font-medium transition-colors',
              videoMode === 'upload'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            Upload vídeo
          </button>
        </div>
      </SettingRow>

      {videoMode === 'url' ? (
        <SettingRow label="URL do vídeo (YouTube ou Vimeo)">
          <Input
            value={props.url ?? ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.url = e.target.value; p.videoSource = 'url'; })}
            className="h-8 text-xs"
            placeholder="https://youtu.be/..."
          />
        </SettingRow>
      ) : (
        <SettingRow label="Arquivo de vídeo">
          <div className="space-y-2">
            {props.videoSource === 'upload' && props.url ? (
              <div className="rounded-md overflow-hidden border bg-muted">
                <video
                  src={props.url}
                  className="w-full h-auto max-h-32"
                  controls={false}
                  muted
                  preload="metadata"
                />
              </div>
            ) : null}
            <label className="cursor-pointer inline-block w-full">
              <input
                type="file"
                accept="video/*"
                className="sr-only"
                onChange={handleVideoUpload}
                disabled={uploading}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                asChild
                disabled={uploading}
              >
                <span>
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Enviando...' : 'Enviar vídeo'}
                </span>
              </Button>
            </label>
            {uploadError && (
              <p className="text-xs text-destructive">{uploadError}</p>
            )}
          </div>
        </SettingRow>
      )}

      <SettingRow label="Legenda">
        <Input
          value={props.caption ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.caption = e.target.value; })}
          className="h-8 text-xs"
          placeholder="Legenda opcional"
        />
      </SettingRow>
    </>
  );
}

function QuoteBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  return (
    <>
      <SettingRow label="Fonte / Citação">
        <Input
          value={props.citation ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.citation = e.target.value; })}
          className="h-8 text-xs"
          placeholder="Autor ou fonte"
        />
      </SettingRow>
      <SettingRow label="Cor da borda">
        <ColorField
          value={props.borderColor ?? '#50E3C2'}
          onChange={(v) => setProp((p) => { p.borderColor = v; })}
        />
      </SettingRow>
    </>
  );
}

function CTABlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  return (
    <>
      <SettingRow label="Texto do botão">
        <Input
          value={props.text ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.text = e.target.value; })}
          className="h-8 text-xs"
          placeholder="Saiba mais"
        />
      </SettingRow>
      <SettingRow label="URL de destino">
        <Input
          value={props.url ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.url = e.target.value; })}
          className="h-8 text-xs"
          placeholder="https://..."
        />
      </SettingRow>
      <SettingRow label="ID do CTA (analytics)">
        <Input
          value={props.ctaId ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.ctaId = e.target.value; })}
          className="h-8 text-xs"
          placeholder="cta-saiba-mais"
        />
      </SettingRow>
      <SettingRow label="Tamanho">
        <Select
          value={props.size ?? 'md'}
          onValueChange={(v: string) => setProp((p) => { p.size = v; })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sm">Pequeno</SelectItem>
            <SelectItem value="md">Médio</SelectItem>
            <SelectItem value="lg">Grande</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>
      <SettingRow label="Cor de fundo">
        <ColorField
          value={props.bgColor ?? '#50E3C2'}
          onChange={(v) => setProp((p) => { p.bgColor = v; })}
        />
      </SettingRow>
      <SettingRow label="Cor do texto">
        <ColorField
          value={props.textColor ?? '#0d0d0d'}
          onChange={(v) => setProp((p) => { p.textColor = v; })}
        />
      </SettingRow>
      <SettingRow label="Largura total">
        <div className="flex items-center gap-2">
          <Switch
            id="cta-fullwidth"
            checked={props.fullWidth ?? false}
            onCheckedChange={(v: boolean) => setProp((p) => { p.fullWidth = v; })}
          />
          <Label htmlFor="cta-fullwidth" className="text-xs cursor-pointer">
            {props.fullWidth ? 'Sim' : 'Não'}
          </Label>
        </div>
      </SettingRow>
    </>
  );
}

function DividerBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  return (
    <>
      <SettingRow label="Cor">
        <ColorField
          value={props.color ?? '#333333'}
          onChange={(v) => setProp((p) => { p.color = v; })}
        />
      </SettingRow>
      <SettingRow label="Espessura (px)">
        <RangeInput
          min={1}
          max={8}
          step={1}
          value={props.thickness ?? 1}
          onChange={(v) => setProp((p) => { p.thickness = v; })}
        />
      </SettingRow>
      <SettingRow label="Estilo">
        <Select
          value={props.style ?? 'solid'}
          onValueChange={(v: string) => setProp((p) => { p.style = v; })}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="solid">Sólido</SelectItem>
            <SelectItem value="dashed">Tracejado</SelectItem>
            <SelectItem value="dotted">Pontilhado</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>
      <SettingRow label="Margem vertical (px)">
        <RangeInput
          min={0}
          max={64}
          step={4}
          value={props.margin ?? 16}
          onChange={(v) => setProp((p) => { p.margin = v; })}
        />
      </SettingRow>
    </>
  );
}

function SpacerBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  return (
    <SettingRow label="Altura (px)">
      <RangeInput
        min={8}
        max={200}
        step={8}
        value={props.height ?? 40}
        onChange={(v) => setProp((p) => { p.height = v; })}
      />
    </SettingRow>
  );
}

function CodeBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  const languages = [
    'javascript', 'typescript', 'html', 'css', 'python',
    'java', 'bash', 'json', 'sql', 'php', 'ruby', 'go', 'rust',
  ];
  return (
    <SettingRow label="Linguagem">
      <Select
        value={props.language ?? 'javascript'}
        onValueChange={(v: string) => setProp((p) => { p.language = v; })}
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {languages.map((lang) => (
            <SelectItem key={lang} value={lang}>{lang}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </SettingRow>
  );
}

function EmbedBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  return (
    <>
      <SettingRow label="HTML do embed">
        <Textarea
          value={props.html ?? ''}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setProp((p) => { p.html = e.target.value; })}
          className="text-xs font-mono min-h-[100px]"
          placeholder="<iframe ...></iframe>"
        />
      </SettingRow>
      <SettingRow label="Legenda">
        <Input
          value={props.caption ?? ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setProp((p) => { p.caption = e.target.value; })}
          className="h-8 text-xs"
          placeholder="Legenda opcional"
        />
      </SettingRow>
    </>
  );
}

function ColumnsBlockSettings({ props, setProp }: { props: AnyProps; setProp: SetPropFn }) {
  return (
    <>
      <SettingRow label="Layout">
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(LAYOUT_PRESETS).map(([key, spans]) => (
            <button
              key={key}
              type="button"
              onClick={() => setProp((p) => { p.layout = key; })}
              className={cn(
                'flex gap-0.5 p-2 rounded border h-10 items-end transition-colors',
                props.layout === key
                  ? 'border-primary bg-primary/10'
                  : 'border-muted-foreground/20 hover:border-muted-foreground/40',
              )}
              title={key}
            >
              {spans.map((span, i) => (
                <div
                  key={i}
                  className={cn(
                    'rounded-sm h-full',
                    props.layout === key ? 'bg-primary' : 'bg-muted-foreground/40',
                  )}
                  style={{ flex: span }}
                />
              ))}
            </button>
          ))}
        </div>
      </SettingRow>
      <SettingRow label="Espaçamento (px)">
        <RangeInput
          min={0}
          max={32}
          step={4}
          value={props.gap ?? 16}
          onChange={(v) => setProp((p) => { p.gap = v; })}
        />
      </SettingRow>
    </>
  );
}

// ---- Block settings registry ----

type SettingsComponent = FC<{ props: AnyProps; setProp: SetPropFn }>;

const settingsMap: Record<string, SettingsComponent> = {
  Texto: TextBlockSettings,
  Título: HeadingBlockSettings,
  Imagem: ImageBlockSettings,
  Vídeo: VideoBlockSettings,
  Citação: QuoteBlockSettings,
  'Botão CTA': CTABlockSettings,
  Divisor: DividerBlockSettings,
  Espaço: SpacerBlockSettings,
  Código: CodeBlockSettings,
  Embed: EmbedBlockSettings,
  Colunas: ColumnsBlockSettings,
};

// ---- Main BlockSettings component ----

export function BlockSettings() {
  const { selected, actions } = useEditor((state) => {
    const selectedSet = state.events.selected;
    const [currentNodeId] = selectedSet;
    if (currentNodeId && state.nodes[currentNodeId]) {
      const node = state.nodes[currentNodeId];

      // When a ColumnContainer ('Coluna') is selected, resolve to its parent
      // ColumnsBlock so settings & delete act on the whole columns layout
      if (node.data.displayName === 'Coluna' && node.data.parent) {
        const parentNode = state.nodes[node.data.parent];
        if (parentNode && parentNode.data.displayName === 'Colunas') {
          return {
            selected: {
              id: node.data.parent,
              displayName: parentNode.data.displayName,
              props: parentNode.data.props,
              deleteTargetId: node.data.parent,
            } as SelectedNodeInfo,
          };
        }
      }

      return {
        selected: {
          id: currentNodeId,
          displayName: node.data.displayName,
          props: node.data.props,
          deleteTargetId: currentNodeId,
        } as SelectedNodeInfo,
      };
    }
    return { selected: null as SelectedNodeInfo | null };
  });

  if (!selected) {
    return (
      <div className="p-4 text-sm text-muted-foreground text-center">
        Selecione um bloco para editar suas propriedades.
      </div>
    );
  }

  const SettingsPanel = settingsMap[selected.displayName];

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">{selected.displayName}</h3>
        {selected.deleteTargetId !== 'ROOT' && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => {
              // Clean up GCS when deleting an image block
              if (selected.displayName === 'Imagem' && selected.props?.mediaId) {
                deleteMedia(selected.props.mediaId).catch(() => {/* best effort */});
              }
              actions.delete(selected.deleteTargetId);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      {SettingsPanel ? (
        <SettingsPanel
          props={selected.props}
          setProp={(cb) => actions.setProp(selected.id, cb)}
        />
      ) : (
        <p className="text-xs text-muted-foreground">
          Sem propriedades configuráveis para este bloco.
        </p>
      )}
    </div>
  );
}
